import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {DataQuery} from '../data-query.model';
import {DataQueryService} from '../data-query.service';
import {DataQueryPermissions} from '../data-query-permissions';
import {User} from '../../../shared-components/user/user.model';
import {UserService} from '../../../shared-components/user/user.service';
import {MenuItem} from '../../../shared-components/controls/menus/menu-item.model';
import { MatDialog } from '@angular/material/dialog';
import {ViewQueryPropertiesDialogComponent} from './view-query-properties-dialog/view-query-properties-dialog.component';
import {DataQueryInformation} from '../data-query-information';
import {Subscription} from 'rxjs';
import {ViewQueriesDialogComponent} from '../view-queries-dialog/view-queries-dialog.component';
import {System} from '../../../shared-components/System';

@Component({
	selector: 'data-query-queries-button',
	templateUrl: './data-query-queries-button.component.html',
	styleUrls: ['./data-query-queries-button.component.css']
})
export class DataQueryQueriesButtonComponent implements OnInit, OnChanges {

	// Interface Properties
	@Input() forFilters = false;

	// Properties
	private _currentQuery: DataQuery;
	information: DataQueryInformation;
	disabledQueryOptions: string[] = [];
	permissions: DataQueryPermissions;
	currentUser: User;
	subscriptions: Subscription[] = [];

	// Class Functions
	constructor(
		private dataQueryService: DataQueryService,
		private userService: UserService,
		private dialog: MatDialog
	) { }

	get currentQuery(): DataQuery {
		return this._currentQuery;
	}

	set currentQuery(value: DataQuery) {
		this.dataQueryService.checkDeactivationAndChange(value);
	}

	get overwriteOptionString(): string {
		let returnString = 'Overwrite ';
		if (this.currentQuery && this.currentQuery.QueryName) {
			returnString += this.currentQuery.QueryName;
		} else {
			returnString += 'Current ' + this.menuName;
		}
		return returnString;
	}

	get menuName(): string {
		return this.forFilters ? 'Filter' : 'Query';
	}

	get menuNamePlural(): string {
		return this.forFilters ? 'Filters' : 'Queries';
	}

	// Event Functions
	ngOnInit() {
		// POST: initializes the data

		// Initializing data members
		this.currentUser = this.userService.currentUser;

		// Maintaining the current query
		this.subscriptions.push(
			this.dataQueryService.observableCurrentQueryStream$.subscribe(currentQuery => {
				this._currentQuery = currentQuery;

				// Checking permissions of the query
				this.checkPermissions();
			}),
			this.dataQueryService.observableInformationStream$.subscribe(information => {
				this.information = information;
			})
		);
	}

	ngOnChanges() {
		// POST: rechecks the permissions
		this.checkPermissions();
	}

	menuClick(menuItem: MenuItem) {
		// PRE: a menu button is clicked
		// POST: performs the appropriate action
		switch (menuItem.itemName) {

			// New Query loads a new query
			case 'New ' + this.menuName:
				this.currentQuery = new DataQuery();
				this.dataQueryService.currentlyActiveStudentsOnly = true;
				break;

			// Import Query opens the import dialog
			case 'Import ' + this.menuName:
				this.dataQueryService.openAddEditQuery(new DataQuery(), false, true);
				break;

			// Overwrite query saves the current query
			case this.overwriteOptionString:
				this.dataQueryService.saveQuery(this.currentQuery, () => {});
				break;

			// Save as new query opens the save dialog
			case 'Save As New ' + this.menuName:
			    if (this.dataQueryService.isStudentSearch) {
                    this.currentQuery.StudentSearch = this.forFilters;
                } else if (this.dataQueryService.isCourseSearch) {
                    this.currentQuery.CourseSearch = this.forFilters;
			    } else if (this.dataQueryService.isStaffSearch) {
                    this.currentQuery.StaffSearch = this.forFilters;
                }
				this.dataQueryService.openAddEditQuery(
					this.currentQuery,
					false,
					false,
					() => {}
				);
				break;

			// Save as My Defaults sets up a default query and saves it
			case 'Save As My Default':
				const query = new DataQuery();
				Object.assign(query, this.currentQuery);
                query.SavedFromID = query.ID;
				query.ID = -1;
				query.QueryName = 'My Defaults';
				query.Description = this.currentQuery.Description;
				query.OwnerID = this.userService.currentUser.Id;
				query.Scope = 'Private';
				query.SelectedSites = this.currentQuery.SelectedSites;
				query.Deleted = false;
				query.StudentSearch = false;	// $scope.isStudentSearch
				this.dataQueryService.saveQuery(query, () => {
					query.QueryName = 'My Default';			// It's supposed to 'My Defaults' on the server, but it's 'My Default' on the client
					this.dataQueryService.currentQuery = query;
					this.information.defaultQuery = query;
				});
				break;

			// Opens the properties dialog for viewing
			case 'View Properties of Current ' + this.menuName:
				this.dialog.open(ViewQueryPropertiesDialogComponent, {
					disableClose: true
				});
				break;

			// Opens a dialog to view all the save queries
			case 'View Saved ' + this.menuNamePlural:
				const dialogBox = this.dialog.open(ViewQueriesDialogComponent, {
					disableClose: true
				});
				this.subscriptions.push(
					dialogBox.afterClosed().subscribe(selectedQuery => {
						if (selectedQuery) {
							const convertedQuery = System.convert<DataQuery>(selectedQuery, new DataQuery());
							let recents: DataQuery[] = [];
							if (this.forFilters) {
                                recents = this.dataQueryService.recentQueriesInContext;
                            } else {
							    recents = this.information.recentQueries;
                            }
							if (recents) {
								for (let i = 0; i < recents.length; i++) {
									if (recents[i].ID == convertedQuery.ID) {
										recents[i] = convertedQuery;
									}
								}
							}
							this.currentQuery = convertedQuery;
						}
					})
				);
				break;

			// Defaults to nothing
			default:
				break;
		}
	}

	// Methods
	menuItems(): string[] {
		// POST: obtains the menu items
		const menuItems =  [
			'New ' + this.menuName,
			this.overwriteOptionString,
			'Save As New ' + this.menuName,
			'View Saved ' + this.menuNamePlural
		];
		if (!this.forFilters) {
			menuItems.splice(1, 0, 'Import ' + this.menuName);
			menuItems.splice(4, 0, 'Save As My Default');
			menuItems.splice(5, 0, 'View Properties of Current ' + this.menuName);
		}
		return menuItems;
	}

	checkPermissions() {
		this.disabledQueryOptions = [];
		this.dataQueryService.getPermissions().subscribe(permissions => {
			this.permissions = permissions;

			// Checking if user can import query
			if (
				!this.permissions.canUserCreatePrivateQuery &&
				!this.permissions.canUserCreateSchoolQuery &&
				!this.permissions.canUserCreateDistrictQuery
			) {
				this.disabledQueryOptions.push('Import Query');
			}

			// Checking if user can overwrite query
			if (!this.dataQueryService.checkUserCanOverwriteQuery() || (this.currentQuery && this.currentQuery.ID < 0)) {
				this.disabledQueryOptions.push(this.overwriteOptionString);
			}

			// Check if user can save as a new query
			if (
				!this.permissions.canUserCreatePrivateQuery &&
				!this.permissions.canUserCreateSchoolQuery &&
				!this.permissions.canUserCreateDistrictQuery
			) {
				this.disabledQueryOptions.push('Save As New Query');
			}

			// Check if user can save as default
			if (!this.permissions.canUserCreatePrivateQuery) {
				this.disabledQueryOptions.push('Save As My Default');
			}
		});
	}
}
